import { useEffect, useState } from 'react';

type Variant = 'card' | 'modal' | 'thumb';

interface Props {
  photo: string | null | undefined;
  restaurantLogoUrl: string | null | undefined;
  restaurantName: string;
  themeColor: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  variant?: Variant;
}

type Tier = 1 | 2 | 3;

function initialTier(photo?: string | null, logo?: string | null): Tier {
  if (photo) return 1;
  if (logo) return 2;
  return 3;
}

export default function ProductImage({
  photo,
  restaurantLogoUrl,
  restaurantName,
  themeColor,
  alt,
  className = '',
  loading = 'lazy',
  variant = 'card'
}: Props) {
  const [tier, setTier] = useState<Tier>(initialTier(photo, restaurantLogoUrl));

  useEffect(() => {
    setTier(initialTier(photo, restaurantLogoUrl));
  }, [photo, restaurantLogoUrl]);

  const advance = () => setTier((t) => (t < 3 ? ((t + 1) as Tier) : 3));

  // In the modal the band has no fixed height — the image drives it, capped so
  // the text zone below keeps room; past the cap object-contain fits the whole
  // photo in (a portrait one must never be centre-cropped), the leftover width
  // filled by a blurred copy of the same photo instead of a bare grey band.
  const frameHeight = variant === 'modal' ? 'h-[clamp(160px,28vh,240px)]' : 'h-full';
  const modalCap = 'max-h-[clamp(160px,28vh,240px)] md:max-h-[min(42vh,360px)]';

  if (tier === 1 && photo) {
    if (variant === 'modal') {
      return (
        <div className={`relative w-full overflow-hidden ${className}`}>
          <div
            aria-hidden="true"
            className="absolute inset-0 scale-110 bg-cover bg-center blur-xl opacity-60"
            style={{ backgroundImage: `url("${photo}")` }}
          />
          <img
            src={photo}
            alt={alt}
            loading={loading}
            onError={advance}
            className={`relative block w-full h-auto min-h-[140px] ${modalCap} object-contain`}
          />
        </div>
      );
    }
    return (
      <img
        src={photo}
        alt={alt}
        loading={loading}
        onError={advance}
        className={`w-full h-full object-cover ${className}`}
      />
    );
  }

  if (tier === 2 && restaurantLogoUrl) {
    const tint = `color-mix(in oklab, ${themeColor || '#1f2937'} 12%, white)`;
    return (
      <div
        className={`w-full ${frameHeight} flex items-center justify-center p-4 ${className}`}
        style={{ background: tint }}
      >
        <img
          src={restaurantLogoUrl}
          alt={alt}
          loading={loading}
          onError={advance}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );
  }

  const text = variant === 'thumb'
    ? restaurantName.slice(0, 2).toUpperCase()
    : restaurantName;

  const textSize =
    variant === 'modal' ? 'text-3xl md:text-4xl'
    : variant === 'thumb' ? 'text-xl'
    : 'text-xl md:text-2xl';

  return (
    <div
      className={`w-full ${frameHeight} flex items-center justify-center p-3 text-center ${className}`}
      style={{ background: themeColor || '#1f2937' }}
      role="img"
      aria-label={alt}
    >
      <span
        className={`font-display text-white font-bold leading-tight break-words ${textSize}`}
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}
      >
        {text}
      </span>
    </div>
  );
}
